import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import { useTheme } from '@/components/ThemeProvider';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import DeleteAccountDialog from '@/pages/settings/DeleteAccountDialog';
import {
  Sun,
  Moon,
  Bell,
  Shield,
  LogOut,
  Trash2,
  ChevronRight,
  User,
  Mail,
  Lock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);

  // Fetch once — hide the password field in the delete dialog for OAuth-only
  // accounts (they have no password to re-enter and the backend accepts that).
  useEffect(() => {
    axios
      .get(`${API}/user/has-password`, { withCredentials: true })
      .then((res) => setHasPassword(!!res.data?.has_password))
      .catch(() => setHasPassword(true));
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleDeleted = async () => {
    // Backend already cleared the cookie — clear local state and go home.
    try {
      await logout();
    } catch { /* ignore — user is gone */ }
    navigate('/', { replace: true });
  };

  const settingsSections = [
    {
      title: 'Appearance',
      items: [
        {
          icon: theme === 'dark' ? Moon : Sun,
          label: 'Dark Mode',
          description: 'Toggle between light and dark theme',
          action: (
            <Switch
              checked={theme === 'dark'}
              onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              data-testid="dark-mode-switch"
            />
          )
        }
      ]
    },
    {
      title: 'Notifications',
      items: [
        {
          icon: Bell,
          label: 'Push Notifications',
          description: 'Receive updates about events and matches',
          action: (
            <Switch
              defaultChecked={true}
              onCheckedChange={() => toast.info('Notification settings saved')}
              data-testid="notifications-switch"
            />
          )
        },
        {
          icon: Mail,
          label: 'Email Notifications',
          description: 'Receive weekly digest and important updates',
          action: (
            <Switch
              defaultChecked={true}
              onCheckedChange={() => toast.info('Email settings saved')}
              data-testid="email-notifications-switch"
            />
          )
        }
      ]
    },
    {
      title: 'Account',
      items: [
        {
          icon: User,
          label: 'Edit Profile',
          description: 'Update your name, photo, and bio',
          action: <ChevronRight className="h-5 w-5 text-muted-foreground" />,
          onClick: () => navigate('/profile')
        },
        {
          icon: Lock,
          label: 'Change Password',
          description: 'Update your password',
          action: <ChevronRight className="h-5 w-5 text-muted-foreground" />,
          onClick: () => navigate('/profile#account-security')
        },
        {
          icon: Shield,
          label: 'Privacy Settings',
          description: 'Control who can see your profile',
          action: <ChevronRight className="h-5 w-5 text-muted-foreground" />,
          onClick: () => navigate('/settings/privacy')
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <Navigation />
      
      <main className="px-4 pt-[68px] pb-8 sm:px-6 sm:pt-20 sm:pb-12 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Settings</h1>
            <p className="text-muted-foreground">
              Manage your account preferences
            </p>
          </div>

          {/* Settings Sections */}
          <div className="space-y-8">
            {settingsSections.map((section) => (
              <div key={section.title}>
                <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">
                  {section.title}
                </h2>
                <div className="rounded-2xl bg-card border border-border/50 overflow-hidden">
                  {section.items.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <React.Fragment key={item.label}>
                        {index > 0 && <Separator />}
                        <div
                          className={`flex items-center justify-between p-4 ${item.onClick ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''}`}
                          onClick={item.onClick}
                          data-testid={`setting-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                              <Icon className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">{item.label}</p>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                          </div>
                          {item.action}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Danger Zone */}
            <div>
              <h2 className="text-sm font-medium text-destructive uppercase tracking-wide mb-4">
                Danger Zone
              </h2>
              <div className="rounded-2xl bg-card border border-destructive/20 overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-4 hover:bg-destructive/5 transition-colors"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                      <LogOut className="h-5 w-5 text-destructive" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-destructive">Log Out</p>
                      <p className="text-sm text-muted-foreground">Sign out of your account</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-destructive" />
                </button>
                <Separator />
                <button
                  className="w-full flex items-center justify-between p-4 hover:bg-destructive/5 transition-colors"
                  onClick={() => setDeleteOpen(true)}
                  data-testid="delete-account-btn"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                      <Trash2 className="h-5 w-5 text-destructive" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-destructive">Delete Account</p>
                      <p className="text-sm text-muted-foreground">Permanently delete your account and data</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-destructive" />
                </button>
              </div>
            </div>
          </div>

          {/* App Info */}
          <div className="mt-12 text-center text-sm text-muted-foreground">
            <p>TheUnityBlaze Connection v1.0.0</p>
            <p className="mt-1">Made with love for outdoor enthusiasts</p>
          </div>
        </div>
      </main>

      <DeleteAccountDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        hasPassword={hasPassword}
        onDeleted={handleDeleted}
      />
    </div>
  );
};

export default Settings;
